import {useMemo} from "react";
import {useMutation} from "@tanstack/react-query";
import {getToken} from "../../../util/tokenCookies.ts";
import {getStripeUrl} from "../../../services/api.ts";
import {notification} from "antd";
import {User} from "../../../context/AuthContext.tsx";
import {MESSAGES, PlanId, SubscriptionDetails, SubscriptionState} from "../utils/silverplus.constants.ts";
import {getCurrentTimeInSeconds} from "../utils/silverplus.utils.ts";

export const useSubscriptionState = (
    user: User | null,
    subscriptionDetails: SubscriptionDetails | undefined,
    currentPlanId: PlanId
): SubscriptionState => {
    return useMemo(() => {
        if (!subscriptionDetails) {
            return {
                type: 'keepCurrentUI',
                shouldShowInfo: currentPlanId !== '0',
                endDate: user?.plan_end_date ?? null
            };
        }

        const currentTime = getCurrentTimeInSeconds();
        const { status, cancel_at } = subscriptionDetails;
        const isCanceled = status === 'canceled';
        const isActive = status === 'active' || status === 'current';
        const hasFuturePlanEnd = (user?.plan_end_date ?? 0) > currentTime;

        if (isCanceled && hasFuturePlanEnd) {
            return {
                type: 'treatAsFree',
                shouldShowInfo: false,
                endDate: null
            };
        }

        if (isActive && cancel_at) {
            return {
                type: 'showAsCanceled',
                shouldShowInfo: true,
                endDate: cancel_at,
                isCanceled: true
            };
        }

        return {
            type: 'keepCurrentUI',
            shouldShowInfo: currentPlanId !== '0',
            endDate: user?.plan_end_date ?? null
        };
    }, [subscriptionDetails, currentPlanId, user?.plan_end_date]);
};

export const usePaymentMutation = () => {
    return useMutation({
        mutationFn: async (planKey: string) => {
            const authToken = getToken();
            if (!authToken) throw new Error('No auth token');

            return await getStripeUrl({
                id: authToken,
                product: planKey,
                redirectPath: 'silverplus'
            });
        },
        onSuccess: (data) => {
            if (data.url) {
                window.location.href = data.url;
            } else {
                notification.error({
                    message: MESSAGES.errors.payment,
                    description: MESSAGES.errors.paymentDescription
                });
            }
        },
        onError: () => {
            notification.error({
                message: MESSAGES.errors.payment,
                description: MESSAGES.errors.paymentDescription
            });
        }
    });
};
